import React, { useMemo } from 'react'
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import McPageHelpers from '../../pages/mc-components/McPageHelpers'
import { useTranslation } from 'react-i18next'
import { MONTH_KEYS } from '../../utils/Constants'

const palette = {
  headerBg: '#e7eef7',
  headerText: '#1f3a6d',
  border: '#d6def2',
  rowHover: '#f5f8ff',
  stripe: '#fbfcff',
  stickyBg: '#fff',
  importsGroup: '#eef3ff',
  exportsGroup: '#fff'
}

const dividerSX = {
  borderLeftWidth: 3,
  borderLeftStyle: 'solid',
  borderLeftColor: palette.headerText
}

const TradeTimeMatrix = ({ data }) => {
  const { t } = useTranslation()
  const timeTags = data?.timeTags ?? []
  const rows = data?.rows ?? []
  const getName = id => McPageHelpers?.findCountryName?.(id) ?? id
  const isYearly = useMemo(() => timeTags.every(t => Number(t) < 10000), [timeTags])

  const parsedMonthly = useMemo(() => {
    if (isYearly) return []
    return timeTags.map(ti => {
      const year = Math.floor(ti / 100)
      const month = ti % 100
      const monthKey = MONTH_KEYS[month - 1]
      return {
        year,
        month,
        labelMonth: t(`months.${monthKey}`)
      }
    })
  }, [timeTags, isYearly])

  const yearGroups = useMemo(() => {
    if (isYearly) return []
    const groups = []
    for (const p of parsedMonthly) {
      const last = groups[groups.length - 1]
      if (!last || last.year !== p.year) groups.push({ year: p.year, count: 1 })
      else last.count += 1
    }
    return groups
  }, [parsedMonthly, isYearly])

  const splitIdx = useMemo(() => {
    if (isYearly) return []
    const idx = []
    for (let i = 1; i < parsedMonthly.length; i++) {
      if (parsedMonthly[i].year !== parsedMonthly[i - 1].year) idx.push(i)
    }
    return idx
  }, [parsedMonthly, isYearly])

  const tableRows = useMemo(() => {
    return rows.map(r => ({
      countryId: r.countryId,
      imports: timeTags.map((_, i) => r.importValues?.[i] ?? 0),
      exports: timeTags.map((_, i) => r.exportValues?.[i] ?? 0)
    }))
  }, [rows, timeTags])

  return (
    <Box>
      <TableContainer sx={{
        borderRadius: 2,
        border: `1px solid ${palette.border}`,
        boxShadow: '0 2px 10px rgba(22,34,66,.06)'
      }}>
        <Table size="small" sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          'th, td': { borderColor: palette.border },
          '& thead th': { fontWeight: 700, color: palette.headerText, whiteSpace: 'nowrap', bgcolor: palette.headerBg },
          '& tbody td': { whiteSpace: 'nowrap' }
        }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  minWidth: 220,
                  bgcolor: palette.headerBg,
                  borderRight: `1px solid ${palette.border}`,
                  fontWeight: 800
                }}
              >{t('market_change_country')} ({t('thousand_dollar')})</TableCell>
              <TableCell align="center" colSpan={timeTags.length} sx={{ fontWeight: 800 }}>
                {t('worldTabs.import')}
              </TableCell>
              <TableCell
                align="center"
                colSpan={timeTags.length}
                sx={{ fontWeight: 800, ...dividerSX }}
              >
                {t('worldTabs.export')}
              </TableCell>
            </TableRow>

            {isYearly
              ? (
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: palette.headerBg,
                      borderRight: `1px solid ${palette.border}`,
                      fontWeight: 600
                    }}
                  />
                  {timeTags.map((y) => (
                    <TableCell key={`i-y-${y}`} align="right" sx={{ fontWeight: 800 }}>
                      {y}
                    </TableCell>
                  ))}
                  {timeTags.map((y, i) => (
                    <TableCell
                      key={`e-y-${y}`}
                      align="right"
                      sx={{ fontWeight: 800, ...(i === 0 ? dividerSX : {}) }}
                    >
                      {y}
                    </TableCell>
                  ))}
                </TableRow>
                )
              : (
                <>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        bgcolor: palette.headerBg,
                        borderRight: `1px solid ${palette.border}`,
                        fontWeight: 600
                      }}
                    />
                    {yearGroups.map((g, i) => (
                      <TableCell
                        key={`y-i-${g.year}`}
                        align="center"
                        colSpan={g.count}
                        sx={{ fontWeight: 800, ...(i > 0 ? dividerSX : {}) }}
                      >
                        {g.year}
                      </TableCell>
                    ))}
                    {yearGroups.map((g, i) => (
                      <TableCell
                        key={`y-e-${g.year}`}
                        align="center"
                        colSpan={g.count}
                        sx={{ fontWeight: 800, ...dividerSX }}
                      >
                        {g.year}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        bgcolor: palette.headerBg,
                        borderRight: `1px solid ${palette.border}`,
                        fontWeight: 600
                      }}
                    />
                    {parsedMonthly.map((p, i) => (
                      <TableCell
                        key={`i-${p.year}-${p.month}`}
                        align="right"
                        sx={{ fontWeight: 700, ...(splitIdx.includes(i) ? dividerSX : {}) }}
                      >
                        {p.labelMonth}
                      </TableCell>
                    ))}
                    {parsedMonthly.map((p, i) => (
                      <TableCell
                        key={`e-${p.year}-${p.month}`}
                        align="right"
                        sx={{ fontWeight: 700, ...((i === 0 || splitIdx.includes(i)) ? dividerSX : {}) }}
                      >
                        {p.labelMonth}
                      </TableCell>
                    ))}
                  </TableRow>
                </>
                )}
          </TableHead>

          <TableBody>
            {tableRows.map((r, rowIdx) => (
              <TableRow
                key={r.countryId}
                hover
                sx={{
                  bgcolor: rowIdx % 2 ? palette.stripe : '#fff',
                  '&:hover': { bgcolor: palette.rowHover }
                }}
              >
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: palette.stickyBg,
                    borderRight: `1px solid ${palette.border}`,
                    fontWeight: 600,
                    color: '#1b2437'
                  }}
                >
                  {getName(r.countryId)}
                </TableCell>

                {r.imports.map((v, i) => (
                  <TableCell
                    key={`iv-${r.countryId}-${i}`}
                    align="right"
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      bgcolor: palette.importsGroup,
                      ...(splitIdx.includes(i) ? dividerSX : {}),
                      '&:hover': {
                        bgcolor: '#dbe7ff',
                        cursor: 'default',
                        transition: 'background 0.2s ease'
                      }
                    }}
                  >
                    {McPageHelpers.formatNumberTimeMatrix(v)}
                  </TableCell>
                ))}

                {r.exports.map((v, i) => (
                  <TableCell
                    key={`ev-${r.countryId}-${i}`}
                    align="right"
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      bgcolor: palette.exportsGroup,
                      ...((i === 0 || splitIdx.includes(i)) ? dividerSX : {}),
                      '&:hover': {
                        bgcolor: '#f9f9f9',
                        cursor: 'default',
                        transition: 'background 0.2s ease'
                      }
                    }}
                  >
                    {McPageHelpers.formatNumberTimeMatrix(v)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default TradeTimeMatrix
